<template>
  <div class="p-4">
    <!-- Top -->
    <div
      class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2"
    >
      <search-box
        title="พิมพ์ชั่วโมงเรียนที่ต้องการค้นหา"
        @typed="getSearchKeyword"
      ></search-box>

      <b-button variant="primary" @click="openCreateModal">
        <i class="fas fa-plus mr-1"></i>
        เพิ่มราคาชั่วโมงเรียน
      </b-button>
    </div>

    <!-- Table -->
    <div class="table-responsive">
      <table class="custom-table">
        <thead>
          <tr>
            <th width="40%">ชั่วโมง</th>
            <th width="40%">ราคา</th>
            <th width="20%" class="text-right"></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in paginatedHourPriceList" :key="index">
            <td>{{ item.hour }} ชั่วโมง</td>

            <td>{{ formatPrice(item.price) }} บาท</td>

            <td class="text-right">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-2"
                @click="openEditModal(item)"
              >
                แก้ไข
              </b-button>

              <b-button
                size="sm"
                variant="outline-danger"
                @click="openDeleteModal(item)"
              >
                <i class="fas fa-trash"></i>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer -->
    <div
      class="d-flex justify-content-between align-items-center mt-4 flex-wrap"
    >
      <div class="d-flex align-items-center">
        <span class="mr-2">แสดง</span>

        <b-form-select
          v-model="perPage"
          :options="pageOptions"
          class="per-page-select"
        />

        <span class="ml-2">
          จาก {{ filteredHourPriceList.length }} รายการ
        </span>
      </div>

      <b-pagination
        v-model="currentPage"
        :total-rows="filteredHourPriceList.length"
        :per-page="perPage"
        first-number
        last-number
        align="right"
      />
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      hide-footer
      centered
      size="lg"
      :title="isEdit ? 'แก้ไขราคาชั่วโมงเรียน' : 'เพิ่มราคาชั่วโมงเรียน'"
    >
      <div class="row mt-3">
        <!-- Hour -->
        <div class="col-md-6">
          <label class="font-weight-bold mb-2">
            ชั่วโมงเรียน
          </label>

          <b-input-group append="ชั่วโมง">
            <b-form-input v-model="form.hour" type="number" />
          </b-input-group>
        </div>

        <!-- Price -->
        <div class="col-md-6">
          <label class="font-weight-bold mb-2">
            ราคา
          </label>

          <b-input-group append="บาท">
            <b-form-input v-model="form.price" type="number" />
          </b-input-group>
        </div>
      </div>

      <div class="text-right mt-5">
        <b-button variant="primary" @click="handleSave" :disabled="isSaveDisabled">
          บันทึก
        </b-button>
      </div>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal v-model="showDeleteModal" centered hide-header hide-footer>
      <div class="text-center py-3">
        <b-icon
          icon="exclamation-triangle"
          variant="warning"
          font-scale="3.5"
          class="mb-3"
        />

        <h5 class="mb-2">
          ลบราคาชั่วโมงเรียน
        </h5>

        <p class="text-muted mb-4">
          ต้องการลบราคาชั่วโมงเรียน {{ selectedItem.hour }} ชั่วโมง หรือไม่?
        </p>

        <div class="d-flex justify-content-center">
          <b-button
            variant="outline-secondary"
            class="mr-2"
            @click="showDeleteModal = false"
          >
            ยกเลิก
          </b-button>

          <b-button variant="danger" @click="handleDelete">
            ลบข้อมูล
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Delete Success Modal -->
    <b-modal
      v-model="showDeleteSuccessModal"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
    >
      <div class="text-center py-4">
        <b-icon
          icon="check-circle"
          variant="success"
          font-scale="4"
          class="mb-4"
        />

        <h4 class="font-weight-bold mb-5">
          ลบราคาชั่วโมงเรียนเรียบร้อยแล้ว
        </h4>

        <b-button variant="primary" @click="showDeleteSuccessModal = false">
          เสร็จสิ้น
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from "@/services/api.js";
import SearchBox from "@/components/shared/SearchBox.vue";

export default {
  components: {
    SearchBox
  },
  data() {
    return {
      searchInput: "",
      searchKeyword: "",

      currentPage: 1,
      perPage: 10,

      showModal: false,
      showDeleteModal: false,
      showDeleteSuccessModal: false,

      isEdit: false,

      selectedItem: {},

      form: {
        id: null,
        hour: "",
        price: "",
      },

      pageOptions: [
        { value: 10, text: "10" },
        { value: 20, text: "20" },
        { value: 50, text: "50" },
      ],

      hourPriceList: [],
    };
  },

  async created() {
    await this.getHourPriceList();
  },

  watch: {
    perPage() {
      this.currentPage = 1;
    },
  },

  computed: {
    filteredHourPriceList() {
      if (!this.searchKeyword) {
        return this.hourPriceList;
      }

      return this.hourPriceList.filter((item) =>
        String(item.hour)
          .toLowerCase()
          .includes(this.searchKeyword.toLowerCase()),
      );
    },

    paginatedHourPriceList() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;

      return this.filteredHourPriceList.slice(start, end);
    },
    isSaveDisabled() {
      return (!this.form.hour || Number(this.form.hour) <= 0 || !this.form.price);
    },
  },

  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat("th-TH").format(price);
    },
    async getHourPriceList() {
      try {
        const response = await api.get("v1/company-course-extend-price/");

        this.hourPriceList = response.data.data
          .filter((item) => item.ccep_type === "EXTEND_ENROLL_HOURS")
          .map((item) => ({
            id: item.ccep_id,
            hour: item.ccep_hour,
            price: item.ccep_price,
          }));
      } catch (error) {
        console.error(error);
      }
    },

    getSearchKeyword(value) {
      this.searchKeyword = value;
      this.currentPage = 1;
    },

    openCreateModal() {
      this.isEdit = false;

      this.form = {
        id: null,
        hour: "",
        price: "",
      };

      this.showModal = true;
    },

    openEditModal(item) {
      this.isEdit = true;

      this.form = {
        id: item.id,
        hour: item.hour,
        price: item.price,
      };

      this.showModal = true;
    },

    async handleSave() {
      try {
        const payload = {
          ccep_type: "EXTEND_ENROLL_HOURS",
          ccep_hour: Number(this.form.hour),
          ccep_price: Number(this.form.price),
        };

        if (this.isEdit) {
          await api.put(
            `v1/company-course-extend-price/${this.form.id}`,
            payload,
          );
        } else {
          await api.post("v1/company-course-extend-price", payload);
        }

        this.showModal = false;

        await this.getHourPriceList();

        this.$bvToast.toast("บันทึกข้อมูลสำเร็จ", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        console.error(error);

        this.$bvToast.toast("ไม่สามารถบันทึกข้อมูลได้", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    openDeleteModal(item) {
      this.selectedItem = item;
      this.showDeleteModal = true;
    },

    async handleDelete() {
      try {
        await api.delete(
          `v1/company-course-extend-price/${this.selectedItem.id}`,
        );

        this.showDeleteModal = false;

        await this.getHourPriceList();

        this.showDeleteSuccessModal = true; 'XADJS$HIFa'
      } catch (error) {
        console.error(error);

        this.$bvToast.toast("ไม่สามารถลบข้อมูลได้", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.search-input {
  width: 300px;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
}

.custom-table thead th {
  font-size: 16px;
  font-weight: 600;
  padding: 14px 8px;
  border-bottom: 1px solid #dcdcdc;
}

.custom-table tbody tr:nth-child(odd) {
  background: #f7f7f7;
}

.custom-table tbody td {
  padding: 18px 8px;
  vertical-align: middle;
}

.per-page-select {
  width: 80px;
}

.gap-2 {
  gap: 10px;
}
</style>
